import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone } from "lucide-react";
import lightBackgroundLogo from "@/assets/brand/bryn-logo-light-bg.png";
import darkBackgroundLogo from "@/assets/brand/bryn-logo-dark-bg.png";

const navigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Projects", href: "/projects" },
  { name: "Team", href: "/team" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const activeLogo = isScrolled ? lightBackgroundLogo : darkBackgroundLogo;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-md py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group shrink-0">
            <img
              src={activeLogo}
              alt="BRYN - Architecture | Construction | Interior"
              className={`w-auto object-contain object-left transition-all duration-300 ${
                isScrolled ? "h-12 md:h-14" : "h-14 md:h-16"
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md relative ${
                  isScrolled
                    ? isActive(item.href)
                      ? "text-primary"
                      : "text-foreground hover:text-primary hover:bg-primary/5"
                    : isActive(item.href)
                    ? "text-primary-foreground"
                    : "text-primary-foreground/80 hover:text-primary-foreground"
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:9986435437"
              className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 ${
                isScrolled
                  ? "text-foreground hover:text-primary"
                  : "text-primary-foreground/90 hover:text-primary-foreground"
              }`}
            >
              <Phone className="w-4 h-4" />
              <span>9986435437</span>
            </a>
            <Button
              asChild
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
            >
              <Link to="/contact#contact-form">Get a Quote</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={`${
                  isScrolled
                    ? "text-foreground hover:bg-primary/10"
                    : "text-primary-foreground hover:bg-primary-foreground/10"
                }`}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] max-w-[88vw] bg-background px-5 sm:px-6">
              <div className="flex h-full flex-col pt-8">
                <div className="mb-6 border-b border-border/70 pb-5">
                  <img
                    src={lightBackgroundLogo}
                    alt="BRYN"
                    className="h-14 w-auto max-w-[200px] object-contain object-left sm:h-16 sm:max-w-[220px]"
                  />
                </div>
                <nav className="flex flex-col gap-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                        isActive(item.href)
                          ? "bg-primary/10 text-primary"
                          : "text-foreground hover:bg-muted hover:text-primary"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto border-t border-border pt-6">
                  <a
                    href="tel:9986435437"
                    className="flex items-center gap-3 rounded-xl px-4 py-3 text-foreground transition-colors hover:bg-muted hover:text-primary"
                  >
                    <Phone className="w-5 h-5" />
                    <span className="font-medium">9986435437</span>
                  </a>
                  <Button asChild className="w-full mt-4 bg-primary hover:bg-primary/90">
                    <Link to="/contact#contact-form" onClick={() => setIsOpen(false)}>
                      Get a Quote
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
